import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExtractionSession, ExtractionStatus } from "../types/multi-extraction";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { Button } from "./animate-ui/components/buttons/button";
import { ArrowUp, ArrowDown, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "../i18n";
import {
  PauseIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EditIcon,
  CopyIcon,
} from "./Icons";
import { Spinner } from "./ui/shadcn-io/spinner";
import { Input } from "./ui/input";

interface ExtractionManagerProps {
  sessions: ExtractionSession[];
  selectedSessionId: string | null;
  activeSessionId: string | null;
  onSelectSession: (id: string | null) => void;
  onDeleteSession: (id: string) => void;
  onCancelExtraction: (id: string) => void;
  onUpdateTitle: (id: string, title: string) => void;
}

const StatusIcon: React.FC<{
  status: ExtractionStatus;
  className?: string;
}> = ({ status, className = "w-4 h-4" }) => {
  switch (status) {
    case ExtractionStatus.IN_PROGRESS:
      return <Spinner variant="circle" size={16} className={className} />;
    case ExtractionStatus.COMPLETED:
      return <CheckCircleIcon className={`${className} text-green-600`} />;
    case ExtractionStatus.FAILED:
      return <XCircleIcon className={`${className} text-red-600`} />;
    case ExtractionStatus.CANCELLED:
      return (
        <ExclamationTriangleIcon className={`${className} text-yellow-600`} />
      );
    case ExtractionStatus.PENDING:
      return <ClockIcon className={`${className} text-gray-500`} />;
    default:
      return <ClockIcon className={`${className} text-gray-500`} />;
  }
};

const getStatusColor = (status: ExtractionStatus): string => {
  switch (status) {
    case ExtractionStatus.IN_PROGRESS:
      return "bg-blue-100 text-blue-800 border-blue-200";
    case ExtractionStatus.COMPLETED:
      return "bg-green-100 text-green-800 border-green-200";
    case ExtractionStatus.FAILED:
      return "bg-red-100 text-red-800 border-red-200";
    case ExtractionStatus.CANCELLED:
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case ExtractionStatus.PENDING:
      return "bg-gray-100 text-gray-800 border-gray-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const ExtractionCard: React.FC<{
  session: ExtractionSession;
  isSelected: boolean;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onCancel: () => void;
  onUpdateTitle: (title: string) => void;
}> = ({
  session,
  isSelected,
  isActive,
  onSelect,
  onDelete,
  onCancel,
  onUpdateTitle,
}) => {
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(session.title || "");

  const formatStartTime = (iso: string) => {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime()) || d.getFullYear() < 2005)
        return { display: "—", title: "Invalid date" };
      const local = d.toLocaleString();
      const offsetMin = d.getTimezoneOffset();
      const offsetHours = -offsetMin / 60;
      const sign = offsetHours >= 0 ? "+" : "-";
      const abs = Math.abs(offsetHours);
      const offsetLabel = `UTC${sign}${abs.toString().padStart(2, "0")}`;
      return { display: local, title: `${d.toISOString()} (${offsetLabel})` };
    } catch {
      return { display: "—", title: "Invalid date" };
    }
  };

  const relativeTime = (iso: string) => {
    try {
      const d = new Date(iso);
      const now = Date.now();
      const diffSec = Math.floor((now - d.getTime()) / 1000);
      if (isNaN(diffSec)) return "";
  if (diffSec < 5) return t("time.just_now", "just now");
  if (diffSec < 60) return t("time.seconds_ago", "{{count}}s ago", { count: diffSec });
      const m = Math.floor(diffSec / 60);
  if (m < 60) return t("time.minutes_ago", "{{count}}m ago", { count: m });
      const h = Math.floor(m / 60);
  if (h < 24) return t("time.hours_ago", "{{count}}h ago", { count: h });
      const days = Math.floor(h / 24);
  if (days < 30) return t("time.days_ago", "{{count}}d ago", { count: days });
      const months = Math.floor(days / 30);
  if (months < 12) return t("time.months_ago", "{{count}}mo ago", { count: months });
  const years = Math.floor(months / 12);
  return t("time.years_ago", "{{count}}y ago", { count: years });
    } catch {
      return "";
    }
  };

  const handleTitleSave = () => {
    if (titleValue.trim() && titleValue !== session.title) {
      onUpdateTitle(titleValue.trim());
    }
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setTitleValue(session.title || "");
    setIsEditingTitle(false);
  };

  const getCurrentProgress = () => {
    if (session.progress.length === 0) return 0;
    return session.progress[session.progress.length - 1].progress_percent || 0;
  };

  const getProcessingTime = () => {
    if (session.duration_seconds) {
      return session.duration_seconds.toFixed(1);
    }
    if (session.endTime && session.startTime) {
      const start = new Date(session.startTime);
      const end = new Date(session.endTime);
      return ((end.getTime() - start.getTime()) / 1000).toFixed(1);
    }
    if (session.results?.processing_time) {
      return session.results.processing_time.toFixed(1);
    }
    return null;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.warn("Copy failed", e);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`
        group rounded-lg border transition-all duration-200 cursor-pointer hover:scale-105
        ${
          isSelected
            ? "ring-2 ring-primary ring-offset-1 border-primary shadow-md"
            : "border-border hover:border-primary/50 hover:shadow-sm"
        }
        ${isActive ? "bg-blue-50/50 dark:bg-blue-950/20" : "bg-card"}
      `}
      onClick={onSelect}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <StatusIcon
              status={session.status}
              className={`flex-shrink-0 w-5 h-5 ${isActive ? "animate-spin" : ""}`}
            />

            {isEditingTitle ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTitleSave();
                    if (e.key === "Escape") handleTitleCancel();
                  }}
                  className="h-8 text-sm flex-1"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleTitleSave();
                  }}
                >
                  <CheckCircleIcon className="w-4 h-4 text-green-600" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleTitleCancel();
                  }}
                >
                  <XCircleIcon className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <h3 className="font-medium text-sm truncate flex-1">
                  {session.title}
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setIsEditingTitle(true);
                    setTitleValue(session.title || "");
                  }}
                >
                  <EditIcon className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Badge
              variant="outline"
              className={`text-xs ${getStatusColor(session.status)}`}
            >
              {t(
                `extraction.status.${session.status.toLowerCase()}`,
                session.status
              )}
            </Badge>
          </div>
        </div>

        {/* Progress Bar */}
        {session.status === ExtractionStatus.IN_PROGRESS && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{t("extraction.progress", "Progress")}</span>
              <span>{getCurrentProgress()}%</span>
            </div>
            <Progress value={getCurrentProgress()} className="h-2" />
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
          <div className="flex items-center gap-4">
            {(() => {
              const f = formatStartTime(session.startTime);
              return (
                <span title={f.title}>
                  {f.display}{" "}
                  <span className="text-muted-foreground/70">
                    ({relativeTime(session.startTime)})
                  </span>
                </span>
              );
            })()}
            {getProcessingTime() && <span>{getProcessingTime()}s</span>}
          </div>
          {session.results && (
            <span>
              {(session.results.codes?.length ?? 0) + (session.results.pcs?.final_codes?.length ?? 0)} {t("extraction.codes", "codes")}
            </span>
          )}
        </div>

        {/* Preview */}
        <div className="text-xs text-muted-foreground mb-3 line-clamp-2 leading-relaxed">
          {session.originalText.substring(0, 120)}
          {session.originalText.length > 120 && "..."}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {session.status === ExtractionStatus.IN_PROGRESS && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onCancel();
                }}
              >
                <PauseIcon className="w-3 h-3 mr-1" />
                {t("extraction.cancel", "Cancel")}
              </Button>
            )}

            {session.status === ExtractionStatus.COMPLETED && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  copyToClipboard(
                    session.results?.codes.map((c) => c.code).join(", ") || ""
                  );
                }}
              >
                <CopyIcon className="w-3 h-3 mr-1" />
                {t("extraction.copy_codes", "Copy Codes")}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-1">
            {(session.status === ExtractionStatus.COMPLETED ||
              session.status === ExtractionStatus.FAILED ||
              session.status === ExtractionStatus.CANCELLED) && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <TrashIcon className="w-3 h-3" />
              </Button>
            )}

            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onSelect();
              }}
            >
              <EyeIcon className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const ExtractionManager: React.FC<ExtractionManagerProps> = ({
  sessions,
  selectedSessionId,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  onCancelExtraction,
  onUpdateTitle,
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<"all" | "active" | "completed">("all");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [displayLimit, setDisplayLimit] = useState(20);
  const MAX_DISPLAY = 20;

  // Apply sorting before filtering for predictable ordering
  const sortedSessions = [...sessions].sort((a, b) => {
    const aTime = new Date(a.startTime).getTime();
    const bTime = new Date(b.startTime).getTime();
    return sortOrder === "newest" ? bTime - aTime : aTime - bTime;
  });

  const filteredSessionsAll = sortedSessions.filter((session) => {
    switch (filter) {
      case "active":
        return (
          session.status === ExtractionStatus.IN_PROGRESS ||
          session.status === ExtractionStatus.PENDING
        );
      case "completed":
        return session.status === ExtractionStatus.COMPLETED;
      default:
        return true;
    }
  });
  const currentShown = Math.min(displayLimit, filteredSessionsAll.length);
  const filteredSessions = filteredSessionsAll.slice(0, currentShown);

  const activeSessions = sessions.filter(
    (s) => s.status === ExtractionStatus.IN_PROGRESS
  );
  const completedSessions = sessions.filter(
    (s) => s.status === ExtractionStatus.COMPLETED
  );

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-1 w-full">
          {/* Filter tabs */}
          <div className="flex items-center gap-1 mt-2">
            {(["all", "active", "completed"] as const).map((filterType) => (
              <Button
                key={filterType}
                size="sm"
                variant={filter === filterType ? "default" : "ghost"}
                className="h-7 px-3 text-xs"
                onClick={() => setFilter(filterType)}
              >
                {t(
                  `extraction.filter.${filterType}`,
                  filterType.charAt(0).toUpperCase() + filterType.slice(1)
                )}
              </Button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 ml-auto">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              {activeSessions.length} {t("extraction.filter.active", "Active")}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              {completedSessions.length}{" "}
              {t("extraction.filter.completed", "Completed")}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() =>
                setSortOrder((o) => (o === "newest" ? "oldest" : "newest"))
              }
              title={
                sortOrder === "newest"
                  ? t("extraction.sort.newest", "Newest first")
                  : t("extraction.sort.oldest", "Oldest first")
              }
              aria-label={
                sortOrder === "newest"
                  ? t("extraction.sort.newest", "Newest first")
                  : t("extraction.sort.oldest", "Oldest first")
              }
            >
              {sortOrder === "newest" ? (
                <ArrowDown className="w-4 h-4" />
              ) : (
                <ArrowUp className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {filteredSessions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <div className="text-4xl mb-2">📄</div>
            <div className="text-sm">
              {filter === "all"
                ? t("extraction.empty.all", "No extractions yet")
                : filter === "active"
                  ? t("extraction.empty.active", "No active extractions")
                  : t("extraction.empty.completed", "No completed extractions")}
            </div>
          </div>
        ) : (
          <AnimatePresence>
            {filteredSessions.map((session) => (
              <ExtractionCard
                key={session.id}
                session={session}
                isSelected={selectedSessionId === session.id}
                isActive={activeSessionId === session.id}
                onSelect={() => onSelectSession(session.id)}
                onDelete={() => onDeleteSession(session.id)}
                onCancel={() => onCancelExtraction(session.id)}
                onUpdateTitle={(title) => onUpdateTitle(session.id, title)}
              />
            ))}
          </AnimatePresence>
        )}
        {filteredSessionsAll.length > MAX_DISPLAY && (
          <div className="mt-1 pt-3 border-t border-border/60">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-[11px] text-muted-foreground text-center sm:text-left">
                {t("extraction.showing_of", "Showing {{current}} of {{total}}", {
                  current: currentShown,
                  total: filteredSessionsAll.length,
                })}
              </div>

              <div className="flex items-center justify-center sm:justify-end gap-2">
                {displayLimit > MAX_DISPLAY && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setDisplayLimit(MAX_DISPLAY)}
                  >
                    <ChevronUp className="w-4 h-4 mr-1" />
                    {t("extraction.show_less", "Show less")}
                  </Button>
                )}

                {filteredSessionsAll.length > currentShown && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 px-4 text-xs"
                    onClick={() =>
                      setDisplayLimit((prev) =>
                        Math.min(prev + MAX_DISPLAY, filteredSessionsAll.length)
                      )
                    }
                  >
                    <ChevronDown className="w-4 h-4 mr-1" />
                    {t("extraction.show_more", "Show more")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
